import { useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { ChevronRight, Sparkles } from 'lucide-react'
import { useRecommendations } from '@/features/recommendations'
import { RecommendationCard } from './recommendation-card'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'

/**
 * Виджет: Персональные рекомендации
 * 
 * Горизонтальный свайп-список с персонализированными рекомендациями.
 * 
 * Алгоритм учитывает:
 * - Записи дневника (особенно с высокими оценками)
 * - Избранное
 * - Историю заказов
 * - Историю просмотров
 */
export function HomeRecommendations() {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const scrollRef = useRef<HTMLDivElement>(null)
  
  const { recommendations, source, isLoading, isPersonalized } = useRecommendations(8)
  
  const handleViewAll = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/catalog/$section', params: { section: 'tea' } })
  }
  
  // Skeleton при загрузке
  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Заголовок skeleton */}
        <div className="flex items-center justify-between px-4">
          <div className="space-y-1.5">
            <div className="h-5 w-32 bg-gray-200 dark:bg-dark-elevated rounded animate-pulse" />
            <div className="h-3 w-48 bg-gray-100 dark:bg-dark-card rounded animate-pulse" />
          </div>
        </div>
        
        {/* Карточки skeleton */}
        <div className="flex gap-3 px-4 overflow-hidden">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[140px] sm:w-[160px] bg-white dark:bg-dark-card rounded-2xl overflow-hidden"
            >
              <div className="aspect-square bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated animate-pulse" />
              <div className="p-2.5 space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-dark-elevated rounded animate-pulse" />
                <div className="h-2 w-2/3 bg-gray-100 dark:bg-dark-card rounded animate-pulse" />
                <div className="flex justify-between items-center pt-1">
                  <div className="h-4 w-16 bg-gray-200 dark:bg-dark-elevated rounded animate-pulse" />
                  <div className="h-8 w-8 bg-gray-200 dark:bg-dark-elevated rounded-full animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }
  
  // Пустое состояние
  if (recommendations.length === 0) {
    return null
  }
  
  return (
    <div className="space-y-3">
      {/* Заголовок секции */}
      <div className="flex items-center justify-between px-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isPersonalized && (
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
              >
                <Sparkles className="w-4 h-4 text-amber-500" />
              </motion.div>
            )}
            <h2 className="text-lg font-bold text-ink dark:text-white">
              {source.title}
            </h2>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {source.subtitle}
          </p>
        </div>
        
        {/* Кнопка "Все" */}
        <button
          type="button"
          onClick={handleViewAll}
          className={cn(
            'flex items-center gap-0.5 px-2 py-1 rounded-lg',
            'text-xs font-medium text-primary dark:text-primary-light',
            'hover:bg-primary/5 dark:hover:bg-primary/10',
            'active:scale-95 transition-all'
          )}
        >
          Все
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      
      {/* Горизонтальный скролл */}
      <div
        ref={scrollRef}
        className={cn(
          'flex gap-3 px-4 overflow-x-auto overscroll-x-contain',
          'scrollbar-hide snap-x snap-mandatory',
          // Padding справа для последней карточки
          'pr-4'
        )}
        style={{
          scrollPaddingLeft: '16px',
          scrollPaddingRight: '16px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {recommendations.map((tea, index) => (
          <div key={tea.id} className="snap-start">
            <RecommendationCard tea={tea} index={index} />
          </div>
        ))}
        
        {/* "Смотреть все" в конце списка */}
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: recommendations.length * 0.05 }}
          type="button"
          onClick={handleViewAll}
          className={cn(
            'flex-shrink-0 w-[100px] sm:w-[120px] aspect-[2/3]',
            'flex flex-col items-center justify-center gap-2',
            'bg-gradient-to-br from-primary/5 to-primary/10',
            'dark:from-primary/10 dark:to-primary/20',
            'rounded-2xl border border-primary/20 dark:border-primary/30',
            'active:scale-95 transition-transform'
          )}
        >
          <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
            <ChevronRight className="w-5 h-5 text-primary dark:text-primary-light" />
          </div>
          <span className="text-xs font-medium text-primary dark:text-primary-light">
            Весь каталог
          </span>
        </motion.button>
      </div>
    </div>
  )
}
